import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useBotWorkspace } from "../../../components/admin/BotWorkspace";
import { ArrowLeft, Save, AlertCircle, CheckCircle2 } from "lucide-react";
import { getBusiness, updateBusiness } from "../../../api/chatbotApi";
import LoadingState from "../../../components/admin/LoadingState";
import ErrorState from "../../../components/admin/ErrorState";

const fields = [
  { field: "name", label: "Bot Name", maxLength: 255 },
  { field: "domain", label: "Slug", maxLength: 100 },
  { field: "industry", label: "Industry", maxLength: 100 },
  { field: "business_type", label: "Business Type", maxLength: 100 },
  { field: "timezone", label: "Timezone", maxLength: 100 },
  { field: "plan", label: "Plan", maxLength: 50 },
];

const getErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((item) => item.msg || String(item)).join("; ");
  return error?.message || fallback;
};

export default function ChatbotEditPage() {
  const { businessId } = useParams();
  const workspace = useBotWorkspace();
  const [bot, setBot] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setBot(null);
    setError("");
    setSuccess(false);
    getBusiness(businessId)
      .then((data) => {
        if (!data?.id || !data?.client_id) throw new Error("Unable to load bot details.");
        if (!active) return;
        setBot(data);
        setForm(Object.fromEntries(fields.map(({ field }) => [field, data[field] ?? ""])));
      })
      .catch((err) => {
        if (active) setError(getErrorMessage(err, "Failed to load bot details."));
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [businessId, attempt]);

  const handleSave = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess(false);
    const payload = Object.fromEntries(fields.map(({ field }) => [field, form[field].trim()]));
    if (!payload.name || !payload.domain) {
      setError("Bot name and slug are required.");
      return;
    }
    if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(payload.domain)) {
      setError("Slug must be lowercase letters, numbers, and hyphens only.");
      return;
    }
    setSaving(true);
    try {
      const updated = await updateBusiness(businessId, payload);
      setBot(updated);
      workspace?.updateBot(updated);
      setSuccess(true);
    } catch (err) {
      setError(getErrorMessage(err, "Failed to save bot details."));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState label="Loading bot details..." />;
  if (!bot) return <ErrorState title="Unable to load bot" description={error} onRetry={() => setAttempt((value) => value + 1)} />;
  const backTo = `/agents/${encodeURIComponent(businessId)}/agent-config`;

  return (
    <div className="w-full space-y-6">
      <Link to={backTo} className="inline-flex items-center gap-1 text-sm font-medium text-slate-500 transition hover:text-slate-900">
        <ArrowLeft size={16} /> Back to bot configuration
      </Link>
      <form onSubmit={handleSave} className="space-y-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6">
        <div className="border-b border-slate-100 pb-5">
          <h1 className="text-2xl font-bold text-slate-900">Edit Bot</h1>
          <p className="mt-1 text-sm text-slate-500">Update details for {bot.name}.</p>
        </div>
        {error && <div role="alert" className="flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700"><AlertCircle size={18} /><span>{error}</span></div>}
        {success && <div role="status" className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700"><CheckCircle2 size={18} /><span>Bot details updated successfully!</span></div>}
        <div className="grid gap-4 md:grid-cols-2">
          {fields.map(({ field, label, maxLength }) => (
            <div key={field}>
              <label htmlFor={`edit-bot-${field}`} className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-700">{label}</label>
              {field === "plan" ? (
                <select
                  id={`edit-bot-${field}`}
                  value={form.plan}
                  required
                  disabled={saving}
                  onChange={(event) => {
                    setForm((current) => ({ ...current, plan: event.target.value }));
                    setSuccess(false);
                  }}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2 text-sm text-slate-900 outline-none transition focus:border-[#096dea] focus:bg-white"
                >
                  {!["starter", "growth", "enterprise"].includes(form.plan) && (
                    <option value={form.plan} disabled>{form.plan || "Select a plan"}</option>
                  )}
                  <option value="starter">Starter</option>
                  <option value="growth">Growth</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              ) : (
              <input
                id={`edit-bot-${field}`}
                type="text"
                value={form[field]}
                required={field === "name" || field === "domain"}
                maxLength={maxLength}
                disabled={saving}
                onChange={(event) => {
                  setForm((current) => ({ ...current, [field]: event.target.value }));
                  setSuccess(false);
                }}
                className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:border-[#096dea] focus:outline-none"
              />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-3 border-t border-slate-100 pt-5">
          {!saving && <Link to={backTo} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50">Back to bot configuration</Link>}
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-xl bg-[#096dea] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#0959bb] disabled:opacity-50">
            <Save size={16} /> {saving ? "Saving..." : "Save Bot Details"}
          </button>
        </div>
      </form>
    </div>
  );
}
